import type { LoyaltyConfig } from '@kilat/shared';

/**
 * Field-scoped write patches for the shared loyalty config doc
 * (`tenants/{tid}/loyaltyProgram/config`).
 *
 * WHY THIS MODULE EXISTS — the lost-update defect it removes:
 *
 * Three surfaces write this one document:
 *   · /loyalty/settings    — earn rules, feature toggles, punch card, referrals
 *   · /loyalty/spin-wheel  — the prize table + the spinWheel feature flag
 *   · /loyalty/promotions  — `promotionsPaused`, the promotions KILL SWITCH,
 *                            read on the money path by `bookTicket` and
 *                            `appendItemsToTicket` (resolvePromotions returns
 *                            zero savings while it is true).
 *
 * Both editors used to save with `repo.set()` — a full-document overwrite built
 * from whatever config snapshot that tab happened to hold. That is a blind
 * read-modify-write against a document a *different* surface also writes, so a
 * save could carry a stale `promotionsPaused` and silently resume every
 * discount an operator had just paused. The window is not only the sub-second
 * one between two simultaneous taps: `firebase.ts` enables
 * `persistentLocalCache`, so a tab that went offline holding a pre-pause
 * snapshot QUEUES its full-document write and flushes it on reconnect —
 * minutes or hours later, still carrying `promotionsPaused: false`.
 *
 * The fix is ownership. Each editor writes ONLY the fields it owns, as a
 * Firestore field patch, so per-field merge preserves everything else no matter
 * how the writes interleave, how long a write was queued, or which tab wins.
 * `promotionsPaused` appears in no editor patch at all, and therefore cannot be
 * clobbered by one. `infra/firestore.rules` enforces the same invariant server
 * side, so a future editor cannot reintroduce the defect.
 *
 * Field ownership for this document:
 *   settings editor  → enabled · pointsPerRM · expiryDays · punchCard ·
 *                      referrals · birthdayBonus · enabledFeatures.* EXCEPT
 *                      spinWheel (dotted keys, one per toggle)
 *   spin-wheel page  → spinWheel · enabledFeatures.spinWheel
 *   kill switch      → promotionsPaused, and nothing else, ever
 *   nobody           → vehicleMultipliers · timeBonuses (server seed only —
 *                      these have no UI and used to survive a save purely by
 *                      accident of the object spread)
 *
 * Kept deliberately pure (no React, no Firestore SDK) so the interleaving is
 * unit-testable, following the `journey-form.ts` / `low-price-check.ts`
 * precedent.
 */

/** One side's reward — reuses the challenge/achievement reward union. */
export type ReferralReward = LoyaltyConfig['referrals']['referrerReward'];

/** Editable draft of one reward side (points amount / voucher / none). */
export interface RewardDraft {
  kind: ReferralReward['kind'];
  /** Points amount as a string (kind === 'points'). */
  amount: string;
  /** Selected voucher template id (kind === 'voucher'). '' = none chosen. */
  voucherTemplateId: string;
}

export function rewardToDraft(r: ReferralReward): RewardDraft {
  return {
    kind: r.kind,
    amount: r.kind === 'points' ? String(r.amount) : '',
    voucherTemplateId: r.kind === 'voucher' ? r.voucherTemplateId : '',
  };
}

export function buildReward(d: RewardDraft): ReferralReward {
  if (d.kind === 'points') {
    const n = Number.parseInt(d.amount, 10);
    return { kind: 'points', amount: Number.isFinite(n) && n > 0 ? n : 0 };
  }
  if (d.kind === 'voucher') return { kind: 'voucher', voucherTemplateId: d.voucherTemplateId };
  return { kind: 'none' };
}

/** Returns an i18n error key if this reward side is misconfigured, else null. */
export function rewardError(d: RewardDraft): string | null {
  if (d.kind === 'points') {
    const n = Number.parseInt(d.amount, 10);
    if (!Number.isFinite(n) || n <= 0) return 'dashboard.loyaltyAdmin.referrals.invalidPoints';
  }
  if (d.kind === 'voucher' && d.voucherTemplateId.trim().length === 0) {
    return 'dashboard.loyaltyAdmin.referrals.noVoucher';
  }
  return null;
}

export interface SettingsDraft {
  enabled: boolean;
  pointsPerRM: string;
  expiryDays: string;
  features: LoyaltyConfig['enabledFeatures'];
  punchWashesRequired: string;
  /** '' = no reward template chosen yet. */
  punchRewardTemplateId: string;
  referrerReward: RewardDraft;
  refereeReward: RewardDraft;
  birthdayReward: RewardDraft;
}

export function settingsDraftFromConfig(c: LoyaltyConfig): SettingsDraft {
  return {
    enabled: c.enabled,
    pointsPerRM: String(c.pointsPerRM),
    expiryDays: String(c.expiryDays),
    features: { ...c.enabledFeatures },
    punchWashesRequired: String(c.punchCard?.washesRequired ?? 10),
    punchRewardTemplateId: c.punchCard?.rewardVoucherTemplateId ?? '',
    referrerReward: rewardToDraft(c.referrals.referrerReward),
    refereeReward: rewardToDraft(c.referrals.refereeReward),
    birthdayReward: rewardToDraft(c.birthdayBonus.reward),
  };
}

/**
 * The exact key set `buildLoyaltySettingsPatch` may emit. Pinned by a test so
 * that adding a field to the settings editor is a conscious decision about
 * ownership rather than an accidental widening of what this page overwrites.
 *
 * `enabledFeatures.*` are DOTTED, never the whole `enabledFeatures` map: the
 * map is co-owned with the spin-wheel page, and writing it wholesale would
 * clobber `enabledFeatures.spinWheel` — the same class of bug one level down.
 */
export const LOYALTY_SETTINGS_PATCH_KEYS: readonly string[] = [
  'birthdayBonus',
  'enabled',
  'enabledFeatures.achievements',
  'enabledFeatures.birthdayBonus',
  'enabledFeatures.challenges',
  'enabledFeatures.punchCard',
  'enabledFeatures.referrals',
  'enabledFeatures.streaks',
  'enabledFeatures.tiers',
  'expiryDays',
  'pointsPerRM',
  'punchCard',
  'referrals',
];

/** The exact key set `buildSpinWheelPatch` may emit. */
export const SPIN_WHEEL_PATCH_KEYS: readonly string[] = ['enabledFeatures.spinWheel', 'spinWheel'];

/**
 * Build the /loyalty/settings save patch.
 *
 * Takes NO live config: needing one was the tell that this page was writing
 * fields it does not own. `pointsPerRM` / `expiryDays` / `washesRequired`
 * arrive already parsed, because the route validates them (and shows a
 * specific error per field) before it decides to save.
 *
 * `punchCard`, `referrals` and `birthdayBonus` are written as whole nested
 * objects. `updateDoc` replaces a nested object wholesale, which is exactly the
 * old behaviour and is intended: the editor rebuilds each of those maps from
 * the form, so clearing the punch-card voucher picker must actually drop
 * `rewardVoucherTemplateId` rather than leave the old id behind.
 */
export function buildLoyaltySettingsPatch(
  draft: SettingsDraft,
  parsed: { pointsPerRM: number; expiryDays: number; washesRequired: number },
): Record<string, unknown> {
  // Preserve a previously-saved washesRequired even while the toggle is off
  // (validation only runs when enabled), so flipping punch card back on
  // doesn't silently reset the operator's chosen card length.
  const savedWashes =
    Number.isFinite(parsed.washesRequired) &&
    parsed.washesRequired >= 2 &&
    parsed.washesRequired <= 50
      ? parsed.washesRequired
      : 10;

  return {
    enabled: draft.enabled,
    pointsPerRM: parsed.pointsPerRM,
    expiryDays: parsed.expiryDays,
    'enabledFeatures.tiers': draft.features.tiers,
    'enabledFeatures.punchCard': draft.features.punchCard,
    'enabledFeatures.achievements': draft.features.achievements,
    'enabledFeatures.challenges': draft.features.challenges,
    'enabledFeatures.referrals': draft.features.referrals,
    'enabledFeatures.birthdayBonus': draft.features.birthdayBonus,
    'enabledFeatures.streaks': draft.features.streaks,
    punchCard: {
      washesRequired: savedWashes,
      ...(draft.punchRewardTemplateId
        ? { rewardVoucherTemplateId: draft.punchRewardTemplateId }
        : {}),
    },
    referrals: {
      referrerReward: buildReward(draft.referrerReward),
      refereeReward: buildReward(draft.refereeReward),
    },
    birthdayBonus: {
      reward: buildReward(draft.birthdayReward),
    },
  };
}

/** Build the /loyalty/spin-wheel save patch. */
export function buildSpinWheelPatch(
  spinWheel: LoyaltyConfig['spinWheel'],
  enabled: boolean,
): Record<string, unknown> {
  return {
    spinWheel,
    'enabledFeatures.spinWheel': enabled,
  };
}
